import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/**
 * One shared `<dialog>`: moves `.details-content` from the clicked `accordion-custom`
 * when a parent has `data-open-content-in-right-drawer` (disabled in theme editor).
 * The panel motion is handled with dialog state classes so the opening stays smooth
 * without measuring the panel width at runtime.
 */
const DRAWER_OPEN_MS = 420;
const DRAWER_CLOSE_MS = 320;
const DRAWER_PANEL_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

const IrisAccordionDrawer = {
  /** @type {HTMLDialogElement | null} */
  _dialog: null,
  /** @type {HTMLElement | null} */
  _scrim: null,
  /** @type {HTMLElement | null} */
  _surface: null,
  /** @type {HTMLElement | null} */
  _body: null,
  /** @type {HTMLElement | null} */
  _title: null,
  /** @type {HTMLButtonElement | null} */
  _closeBtn: null,
  /** @type {Comment | null} */
  _placeholder: null,
  /** @type {AccordionCustom | null} */
  _host: null,
  /** @type {HTMLElement | null} */
  _trigger: null,
  /** @type {number} */
  _motionTimer: 0,
  /** @type {number} */
  _openFrame: 0,
  /** @type {number} */
  _openFrameNested: 0,

  _isReducedMotion() {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
  },

  _lockScroll() {
    document.documentElement.classList.add('iris-accordion-drawer-open');
  },

  _unlockScroll() {
    document.documentElement.classList.remove('iris-accordion-drawer-open');
  },

  _setState(state) {
    const dialog = this._dialog;
    if (!dialog) return;

    dialog.dataset.state = state;
  },

  _prepareSurfaceMotion() {
    const surface = this._surface;
    if (!(surface instanceof HTMLElement)) return;

    surface.style.willChange = 'transform, opacity';
  },

  _finishSurfaceMotion() {
    const surface = this._surface;
    if (!(surface instanceof HTMLElement)) return;

    surface.style.removeProperty('will-change');
  },

  _clearPendingMotion() {
    if (this._motionTimer) {
      window.clearTimeout(this._motionTimer);
      this._motionTimer = 0;
    }

    if (this._openFrame) {
      window.cancelAnimationFrame(this._openFrame);
      this._openFrame = 0;
    }

    if (this._openFrameNested) {
      window.cancelAnimationFrame(this._openFrameNested);
      this._openFrameNested = 0;
    }

    this._finishSurfaceMotion();
  },

  _syncHostExpanded(expanded) {
    const host = this._host;
    if (!host) return;

    host.summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  },

  _ensure() {
    if (this._dialog) return;

    const dialog = document.createElement('dialog');
    dialog.className = 'iris-accordion-drawer';
    dialog.setAttribute('aria-modal', 'true');

    const scrim = document.createElement('div');
    scrim.className = 'iris-accordion-drawer__scrim';
    scrim.setAttribute('aria-hidden', 'true');

    const surface = document.createElement('div');
    surface.className = 'iris-accordion-drawer__surface';

    const header = document.createElement('div');
    header.className = 'iris-accordion-drawer__header';

    const title = document.createElement('h2');
    title.className = 'iris-accordion-drawer__title';
    title.id = 'iris-accordion-drawer-title';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'iris-accordion-drawer__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';

    const body = document.createElement('div');
    body.className = 'iris-accordion-drawer__body';

    header.appendChild(title);
    header.appendChild(closeBtn);
    surface.appendChild(header);
    surface.appendChild(body);
    dialog.appendChild(scrim);
    dialog.appendChild(surface);

    scrim.addEventListener('click', () => this.closeAnimated());

    dialog.addEventListener('close', () => this._onDialogClose());
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.closeAnimated();
    });
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closeAnimated();
    });

    document.body.appendChild(dialog);

    this._dialog = dialog;
    this._scrim = scrim;
    this._surface = surface;
    this._title = title;
    this._closeBtn = closeBtn;
    this._body = body;
  },

  /**
   * @param {HTMLElement} host
   */
  _closeLabelFromHost(host) {
    const root = host.closest('[data-open-content-in-right-drawer]');
    const label = (root instanceof HTMLElement) ? root.dataset.accordionDrawerCloseLabel : undefined;
    return label && label.trim() ? label.trim() : 'Close';
  },

  _animateBodySwap() {
    const body = this._body;
    if (!(body instanceof HTMLElement) || this._isReducedMotion()) return;

    body.getAnimations().forEach((animation) => animation.cancel());
    body.animate(
      [
        { opacity: 0, transform: 'translateX(0.75rem)' },
        { opacity: 1, transform: 'translateX(0)' },
      ],
      {
        duration: 220,
        easing: DRAWER_PANEL_EASING,
      }
    );
  },

  _restoreContent() {
    if (!this._host) return;

    const content = this._body?.querySelector('.details-content');
    if (content instanceof HTMLElement) {
      const details = this._host.details;
      if (this._placeholder && this._placeholder.parentNode) {
        this._placeholder.parentNode.replaceChild(content, this._placeholder);
      } else {
        details.appendChild(content);
      }
    }

    this._syncHostExpanded(false);
    this._placeholder = null;
    this._host = null;
  },

  _onDialogClose() {
    this._clearPendingMotion();
    this._setState('closed');
    requestAnimationFrame(() => this._unlockScroll());

    this._restoreContent();
    const trigger = this._trigger;
    this._trigger = null;
    trigger?.focus();
  },

  closeAnimated() {
    const dialog = this._dialog;
    if (!dialog?.open) return;

    this._clearPendingMotion();

    if (this._isReducedMotion()) {
      dialog.close();
      return;
    }

    this._prepareSurfaceMotion();
    this._setState('closing');
    this._motionTimer = window.setTimeout(() => {
      this._motionTimer = 0;
      dialog.close();
    }, DRAWER_CLOSE_MS + 40);
  },

  /**
   * @param {AccordionCustom} host
   */
  open(host) {
    this._ensure();

    const content = host.details.querySelector('.details-content');
    if (!(content instanceof HTMLElement)) return;

    const dialog = this._dialog;
    if (!dialog) return;

    const hadOpen = dialog.open;
    const isSameHost = hadOpen && this._host === host;
    if (isSameHost) return;

    this._clearPendingMotion();
    this._restoreContent();

    this._trigger = host.summary;
    this._host = host;
    this._syncHostExpanded(true);

    this._placeholder = document.createComment('iris-accordion-drawer-placeholder');
    content.parentNode?.insertBefore(this._placeholder, content);
    this._body?.appendChild(content);

    const heading = host.details.dataset.accordionHeading?.trim() || '';
    if (this._title) {
      this._title.textContent = heading;
      this._dialog?.setAttribute('aria-labelledby', 'iris-accordion-drawer-title');
    }

    if (this._closeBtn) {
      this._closeBtn.setAttribute('aria-label', this._closeLabelFromHost(host));
    }

    if (this._body) {
      this._body.scrollTop = 0;
    }

    if (hadOpen) {
      this._animateBodySwap();
      this._closeBtn?.focus();
      return;
    }

    this._lockScroll();
    this._setState('closed');
    dialog.showModal();

    if (this._isReducedMotion()) {
      this._setState('open');
      this._closeBtn?.focus();
      return;
    }

    this._prepareSurfaceMotion();
    this._openFrame = requestAnimationFrame(() => {
      this._openFrame = 0;
      this._openFrameNested = requestAnimationFrame(() => {
        this._openFrameNested = 0;
        if (!dialog.open) return;

        this._setState('open');
        this._closeBtn?.focus();
        this._motionTimer = window.setTimeout(() => {
          this._motionTimer = 0;
          this._finishSurfaceMotion();
        }, DRAWER_OPEN_MS + 60);
      });
    });
  },
};

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** @type {boolean} */
  #drawerMode = false;

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#drawerMode = Boolean(this.closest('[data-open-content-in-right-drawer]')) && !window.Shopify?.designMode;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    if (this.#drawerMode) {
      this.summary.setAttribute('aria-haspopup', 'dialog');
      this.summary.setAttribute('aria-expanded', 'false');
      this.summary.addEventListener('click', this.#handleDrawerSummaryClick, { capture: true, signal });
      this.summary.addEventListener('keydown', this.#handleDrawerSummaryKeydown, { signal });
      this.details.addEventListener('toggle', this.#handleDrawerDetailsToggle, { signal });
    } else {
      this.summary.removeAttribute('aria-haspopup');
      this.summary.removeAttribute('aria-expanded');
      this.summary.addEventListener('click', this.handleClick, { signal });
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    if (this.#drawerMode && IrisAccordionDrawer._host === this) {
      if (IrisAccordionDrawer._dialog?.open) {
        IrisAccordionDrawer._dialog.close();
      } else {
        IrisAccordionDrawer._restoreContent();
        IrisAccordionDrawer._unlockScroll();
      }
    }
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
    }
  };

  /** @param {MouseEvent} event */
  #handleDrawerSummaryClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    IrisAccordionDrawer.open(this);
  };

  /** @param {KeyboardEvent} event */
  #handleDrawerSummaryKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      IrisAccordionDrawer.open(this);
    }
  };

  #handleDrawerDetailsToggle = () => {
    if (this.details.open) {
      this.details.open = false;
    }
  };

  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  #setDefaultOpenState() {
    if (this.#drawerMode) {
      this.details.open = false;
      return;
    }

    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
